// src/applicants/ApplicantInterviews.js
import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  VideoCall as VideoCallIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as PendingIcon,
  Error as ErrorIcon,
  Download as DownloadIcon,
  UploadFile as UploadIcon,
  Person as PersonIcon,
  Stars as PointsIcon,
} from '@mui/icons-material';
import api from '../config/api';
import useStore from '../config/store';
import { useTranslation } from 'react-i18next';

const ApplicantInterviews = ({ applicant }) => {
  const { authToken, project, organization, showAlert } = useStore();
  const { t } = useTranslation();

  const STATUS_CONFIG = {
    pending: {
      label: t('interviews.status.pending'),
      color: 'info',
      icon: <PendingIcon fontSize="small" />,
    },
    pending_assignment: {
      label: t('interviews.status.pendingAssignment'),
      color: 'warning',
      icon: <PersonIcon fontSize="small" />,
    },
    completed: {
      label: t('interviews.status.completed'),
      color: 'success',
      icon: <CheckCircleIcon fontSize="small" />,
    },
    error: {
      label: t('interviews.status.error'),
      color: 'error',
      icon: <ErrorIcon fontSize="small" />,
    },
  };

  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingInvite, setLoadingInvite] = useState(false);
  const [meetingUrl, setMeetingUrl] = useState('');
  const [meetingUrlError, setMeetingUrlError] = useState('');

  // Subir transcripción/informe existente
  const [uploadFile, setUploadFile] = useState(null);
  const [interviewDate, setInterviewDate] = useState('');
  const [loadingUpload, setLoadingUpload] = useState(false);

  const [usageCheck, setUsageCheck] = useState(null);
  const [loadingUsageCheck, setLoadingUsageCheck] = useState(true);

  const [assigningId, setAssigningId] = useState(null);
  const [selectedIntervieweeId, setSelectedIntervieweeId] = useState('');
  const [loadingAssign, setLoadingAssign] = useState(false);

  const checkUsage = async () => {
    if (!organization || !authToken) {
      setLoadingUsageCheck(false);
      return;
    }
    try {
      setLoadingUsageCheck(true);
      const { data } = await api.get(
        `/organizations/${organization}/usage/check`,
        {
          params: { action: 'interview' },
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
      if (data.response) {
        setUsageCheck(data.response);
      } else {
        setUsageCheck({ canPerform: true });
      }
    } catch (err) {
      console.error('Error verificando puntos:', err);
      setUsageCheck({ canPerform: true });
    } finally {
      setLoadingUsageCheck(false);
    }
  };

  const fetchInterviews = async () => {
    if (!applicant?.uid) return;
    try {
      setLoading(true);
      const res = await api.get(
        `/applicants/${applicant.uid}/interviews`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (res.data.errorCode === 0) {
        const data = res.data.response?.data;
        setInterviews(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error cargando entrevistas:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkUsage();
    fetchInterviews();
  }, [applicant?.uid]);

  const handleInviteBot = async () => {
    if (!meetingUrl.trim()) {
      setMeetingUrlError(t('interviews.meetingUrlRequired'));
      return;
    }
    setMeetingUrlError('');
    setLoadingInvite(true);
    try {
      const res = await api.post(
        `/applicants/${applicant.uid}/interviews/invite-bot`,
        {
          meetingUrl: meetingUrl.trim(),
          projectId: project,
          organizationId: organization,
        },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (res.data.errorCode === 0) {
        showAlert(t('interviews.botInvitedSuccess'), 'success');
        setMeetingUrl('');
        fetchInterviews();
      } else {
        showAlert(res.data.response?.message || t('interviews.inviteBotError'), 'error');
      }
    } catch (err) {
      showAlert(t('interviews.inviteBotError'), 'error');
    } finally {
      setLoadingInvite(false);
    }
  };

  const handleUploadTranscript = async () => {
    if (!uploadFile) {
      showAlert(t('interviews.upload.fileRequired'), 'warning');
      return;
    }
    setLoadingUpload(true);
    try {
      const formData = new FormData();
      formData.append('transcript', uploadFile);
      formData.append('projectId', project);
      formData.append('organizationId', organization);
      if (applicant?.name) {
        formData.append('intervieweeName', applicant.name);
      }
      if (interviewDate) {
        formData.append('interviewDate', interviewDate);
      }

      const res = await api.post(
        `/applicants/${applicant.uid}/interviews/upload-transcript`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (res.data.errorCode === 0) {
        showAlert(t('interviews.upload.success'), 'success');
        setUploadFile(null);
        setInterviewDate('');
        fetchInterviews();
      } else {
        showAlert(res.data.response?.message || t('interviews.upload.error'), 'error');
      }
    } catch (err) {
      showAlert(t('interviews.upload.error'), 'error');
    } finally {
      setLoadingUpload(false);
    }
  };

  const handleAssignParticipants = async (interview) => {
    if (!selectedIntervieweeId) {
      showAlert(t('interviews.selectIntervieweeRequired'), 'warning');
      return;
    }

    const interviewee = interview.participants.find(p => p.id === Number(selectedIntervieweeId));

    setLoadingAssign(true);
    try {
      const res = await api.put(
        `/applicants/${applicant.uid}/interviews/${interview.uid}/assign-participants`,
        {
          intervieweeId: interviewee.id,
          intervieweeName: interviewee.name,
          applicantName: applicant.name,
          meetingDate: interview.createdAt?.seconds
            ? new Date(interview.createdAt.seconds * 1000).toISOString()
            : null,
        },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (res.data.errorCode === 0) {
        showAlert(t('interviews.assignedSuccess'), 'success');
        setAssigningId(null);
        setSelectedIntervieweeId('');
        fetchInterviews();
      } else {
        showAlert(res.data.response?.message || t('interviews.assignError'), 'error');
      }
    } catch (err) {
      showAlert(t('interviews.assignError'), 'error');
    } finally {
      setLoadingAssign(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '—';
    let date;
    if (timestamp?.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else if (timestamp?._seconds) {
      date = new Date(timestamp._seconds * 1000);
    } else {
      date = new Date(timestamp);
    }
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleString('es-CL');
  };

  const canInvite = !loadingUsageCheck && usageCheck?.canPerform !== false;

  return (
    <Box>
      {/* Aviso de puntos */}
      {!loadingUsageCheck && usageCheck && (
        <Alert
          severity={usageCheck.canPerform === false ? 'error' : 'info'}
          icon={<PointsIcon />}
          sx={{ mb: 2 }}
        >
          {usageCheck.canPerform === false
            // SERVICE_NOT_AVAILABLE ≠ falta de puntos: el módulo no está en
            // points_system.available_services de la organización (o en su plan)
            ? (usageCheck.reason === 'SERVICE_NOT_AVAILABLE'
              ? t('interviews.serviceNotAvailable')
              : t('interviews.noPoints'))
            : usageCheck.unlimited
              ? t('interviews.pointsCostUnlimited', { cost: 2 })
              : t('interviews.pointsCost', { cost: 2, remaining: usageCheck.remaining })
          }
        </Alert>
      )}

      {/* Invitar bot */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 2 }}>
          {t('interviews.inviteBotTitle')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <TextField
            label={t('interviews.meetingUrlLabel')}
            value={meetingUrl}
            onChange={(e) => setMeetingUrl(e.target.value)}
            error={!!meetingUrlError}
            helperText={meetingUrlError || t('interviews.meetingUrlHelper')}
            fullWidth
            size="small"
            placeholder="https://meet.google.com/xxx-xxxx-xxx"
            disabled={!canInvite}
          />
          <Button
            variant="contained"
            startIcon={loadingInvite ? <CircularProgress size={16} color="inherit" /> : <VideoCallIcon />}
            onClick={handleInviteBot}
            disabled={loadingInvite || !canInvite}
            sx={{ whiteSpace: 'nowrap', minWidth: 160 }}
          >
            {loadingInvite ? t('interviews.inviting') : t('interviews.inviteBot')}
          </Button>
        </Box>
      </Paper>

      {/* Subir transcripción/informe existente */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 0.5 }}>
          {t('interviews.upload.title')}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          {t('interviews.upload.hint')}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5, flexWrap: 'wrap' }}>
          <Button variant="outlined" component="label" startIcon={<UploadIcon />} size="small">
            {t('interviews.upload.chooseFile')}
            <input
              hidden
              type="file"
              accept=".txt,.pdf,.doc,.docx"
              onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
            />
          </Button>
          {uploadFile && (
            <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>
              {uploadFile.name}
            </Typography>
          )}
        </Box>

        <TextField
          type="date"
          size="small"
          label={t('interviews.upload.dateLabel')}
          InputLabelProps={{ shrink: true }}
          value={interviewDate}
          onChange={(e) => setInterviewDate(e.target.value)}
          sx={{ mb: 1.5, width: 220 }}
        />

        <Button
          variant="contained"
          startIcon={loadingUpload ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
          onClick={handleUploadTranscript}
          disabled={loadingUpload || !uploadFile}
          fullWidth
        >
          {loadingUpload ? t('interviews.upload.uploading') : t('interviews.upload.submit')}
        </Button>
      </Paper>

      {/* Lista de entrevistas */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="medium">
          {t('interviews.title')} ({interviews.length})
        </Typography>
        <Button
          size="small"
          startIcon={<RefreshIcon />}
          onClick={fetchInterviews}
          disabled={loading}
        >
          {t('interviews.refresh')}
        </Button>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      )}

      {!loading && interviews.length === 0 && (
        <Alert severity="info">
          {t('interviews.noInterviews')}
        </Alert>
      )}

      {!loading && interviews.map((interview) => {
        const statusCfg = STATUS_CONFIG[interview.status] || STATUS_CONFIG.pending;
        const isAssigning = assigningId === interview.uid;

        return (
          <Paper key={interview.uid} variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <VideoCallIcon color="action" fontSize="small" />
                <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 300 }}>
                  {interview.meetingUrl}
                </Typography>
              </Box>
              <Chip
                icon={statusCfg.icon}
                label={statusCfg.label}
                color={statusCfg.color}
                size="small"
              />
            </Box>

            <Typography variant="caption" color="text.secondary">
              {formatDate(interview.createdAt)}
            </Typography>

            {interview.status === 'pending_assignment' && (
              <Box sx={{ mt: 2 }}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {t('interviews.detectedParticipants')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  {interview.participants?.map((p) => (
                    <Chip key={p.id} label={p.name} size="small" variant="outlined" />
                  ))}
                </Box>

                {!isAssigning ? (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<PersonIcon />}
                    onClick={() => {
                      setAssigningId(interview.uid);
                      setSelectedIntervieweeId('');
                    }}
                  >
                    {t('interviews.assignRoles')}
                  </Button>
                ) : (
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                      <InputLabel>{t('interviews.whoIsInterviewee')}</InputLabel>
                      <Select
                        value={selectedIntervieweeId}
                        onChange={(e) => setSelectedIntervieweeId(e.target.value)}
                        label={t('interviews.whoIsInterviewee')}
                      >
                        {interview.participants?.map((p) => (
                          <MenuItem key={p.id} value={p.id}>
                            {p.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => handleAssignParticipants(interview)}
                      disabled={loadingAssign || !selectedIntervieweeId}
                      startIcon={loadingAssign ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon />}
                    >
                      {t('interviews.confirm')}
                    </Button>
                    <Button
                      size="small"
                      onClick={() => {
                        setAssigningId(null);
                        setSelectedIntervieweeId('');
                      }}
                    >
                      {t('cancel')}
                    </Button>
                  </Box>
                )}
              </Box>
            )}

            {interview.status === 'completed' && interview.transcriptTxtUrl && (
              <Box sx={{ mt: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  href={interview.transcriptTxtUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('interviews.viewTranscript')}
                </Button>
              </Box>
            )}

            {interview.status === 'error' && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {interview.errorMessage || t('interviews.processingError')}
              </Alert>
            )}
          </Paper>
        );
      })}
    </Box>
  );
};

export default ApplicantInterviews;